import { useMemo } from 'react';

const trimestreMonths = {
  1: [1, 2, 3],
  2: [4, 5, 6],
  3: [7, 8, 9],
  4: [10, 11, 12]
};

/**
 * Calcule les agrégations par trimestre (jours, CA, refacturation).
 * La refacturation utilise le TMP par défaut : (joursNormaux × TMP) + caTmpExcl
 *
 * @param {Array} tempsByYear - Temps filtrés par année
 * @param {number} yearFilter - Année sélectionnée
 * @param {Object} calculMap - Map des Calcul TMP { "2025-Q1": { raw, tmp, ... } }
 * @returns {Object} { [1]: trimestreData, [2]: ..., [3]: ..., [4]: ... }
 */
export function useTrimestreAggregation(tempsByYear, yearFilter, calculMap) {
  return useMemo(() => {
    const result = {};

    [1, 2, 3, 4].forEach(q => {
      result[q] = aggregateForTrimestre(q, tempsByYear, yearFilter, calculMap);
    });

    return result;
  }, [tempsByYear, yearFilter, calculMap]);
}

function aggregateForTrimestre(q, tempsByYear, yearFilter, calculMap) {
  const months = trimestreMonths[q];

  // Identifier les consultants qui ont des temps sur ce trimestre
  const tempsDuTrimestre = tempsByYear.filter(r => months.includes(Number(r.fields?.Mois)));
  const consultantsDuTrimestre = Array.from(new Set(
    tempsDuTrimestre.map(r => r.consultantNom).filter(nom => nom && nom !== '-')
  )).sort();

  // Données par mois et par consultant
  const monthsData = months.map(m => {
    const recsOfMonth = tempsByYear.filter(r => Number(r.fields?.Mois) === m);

    // Calculer par consultant (séparation normaux / tmpexcl)
    const byConsultant = {};
    consultantsDuTrimestre.forEach(c => {
      const recsConsultant = recsOfMonth.filter(r => r.consultantNom === c);

      const recsNormaux = recsConsultant.filter(r => !r.fields?.tmpexcl);
      const recsTmpExcl = recsConsultant.filter(r => Boolean(r.fields?.tmpexcl));

      const daysNormaux = recsNormaux.reduce((s, r) => s + (parseFloat(r.fields?.['Nombre de jours']) || 0), 0);
      const caNormaux = recsNormaux.reduce((s, r) => {
        const caField = r.fields?.['CA calculé'] ?? r.fields?.['CA calculé €'] ?? null;
        if (caField != null && caField !== '') return s + (parseFloat(caField) || 0);
        const d = parseFloat(r.fields?.['Nombre de jours']) || 0;
        const tjm = parseFloat(r.fields?.['Taux journalier (€)']) || 0;
        return s + d * tjm;
      }, 0);

      const daysTmpExcl = recsTmpExcl.reduce((s, r) => s + (parseFloat(r.fields?.['Nombre de jours']) || 0), 0);
      const caTmpExcl = recsTmpExcl.reduce((s, r) => {
        const caField = r.fields?.['CA calculé'] ?? r.fields?.['CA calculé €'] ?? null;
        if (caField != null && caField !== '') return s + (parseFloat(caField) || 0);
        const d = parseFloat(r.fields?.['Nombre de jours']) || 0;
        const tjm = parseFloat(r.fields?.['Taux journalier (€)']) || 0;
        return s + d * tjm;
      }, 0);

      const days = daysNormaux + daysTmpExcl;
      const ca = caNormaux + caTmpExcl;

      byConsultant[c] = { days, ca, daysNormaux, caNormaux, daysTmpExcl, caTmpExcl };
    });

    // Totaux du mois
    const totalDays = Object.values(byConsultant).reduce((s, v) => s + v.days, 0);
    const totalCA = Object.values(byConsultant).reduce((s, v) => s + v.ca, 0);
    const totalDaysNormaux = Object.values(byConsultant).reduce((s, v) => s + v.daysNormaux, 0);
    const totalCANormaux = Object.values(byConsultant).reduce((s, v) => s + v.caNormaux, 0);

    return { month: m, byConsultant, totalDays, totalCA, totalDaysNormaux, totalCANormaux };
  });

  // Totaux du trimestre par consultant
  const totalsByConsultant = {};
  consultantsDuTrimestre.forEach(c => {
    totalsByConsultant[c] = {
      days: monthsData.reduce((s, md) => s + md.byConsultant[c].days, 0),
      ca: monthsData.reduce((s, md) => s + md.byConsultant[c].ca, 0),
      daysNormaux: monthsData.reduce((s, md) => s + md.byConsultant[c].daysNormaux, 0),
      caNormaux: monthsData.reduce((s, md) => s + md.byConsultant[c].caNormaux, 0),
      daysTmpExcl: monthsData.reduce((s, md) => s + md.byConsultant[c].daysTmpExcl, 0),
      caTmpExcl: monthsData.reduce((s, md) => s + md.byConsultant[c].caTmpExcl, 0)
    };
  });

  const trimesterTotalDays = monthsData.reduce((s, m) => s + m.totalDays, 0);
  const trimesterTotalCA = monthsData.reduce((s, m) => s + m.totalCA, 0);

  // TMP par défaut du trimestre
  const calcKey = `${yearFilter}-Q${q}`;
  const tmpEntry = calculMap[calcKey];
  const tmpDefault = tmpEntry?.tmp ?? null;

  // Refacturation par mois : (joursNormaux × TMP) + caTmpExcl
  monthsData.forEach(md => {
    md.refacByConsultant = {};
    let monthTotalRefac = 0;
    let hasRefac = false;

    consultantsDuTrimestre.forEach(c => {
      if (tmpDefault != null) {
        const refacNormale = md.byConsultant[c].daysNormaux * parseFloat(tmpDefault);
        const refacTmpExcl = md.byConsultant[c].caTmpExcl || 0;
        const refac = refacNormale + refacTmpExcl;

        md.refacByConsultant[c] = refac;
        monthTotalRefac += refac;
        hasRefac = true;
      } else {
        md.refacByConsultant[c] = null;
      }
    });

    md.totalRefac = hasRefac ? monthTotalRefac : null;
  });

  // Refacturation totale du trimestre
  let trimesterRefac = null;
  const monthRefacValues = monthsData.map(md => md.totalRefac).filter(v => v != null);
  if (monthRefacValues.length > 0) {
    trimesterRefac = monthRefacValues.reduce((s, v) => s + v, 0);
  }

  // Totaux refacturation par consultant
  const refacByConsultant = {};
  consultantsDuTrimestre.forEach(c => {
    refacByConsultant[c] = monthsData.reduce((sum, md) => {
      return sum + (md.refacByConsultant[c] || 0);
    }, 0);
  });

  return {
    monthsData,
    totalsByConsultant,
    refacByConsultant,
    trimesterTotalDays,
    trimesterTotalCA,
    trimesterRefac,
    tmpValue: tmpDefault,
    tmpRecord: tmpEntry?.raw ?? null,
    consultantsDuTrimestre
  };
}
